import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { useUsersOptions } from "api/hooks/useUsers";
import { EmailRecipientApi } from "api/types/EmailRecipient";
import { RecipientRequest } from "api/scheduledemails/scheduledemailsApi";
import { useCourseGroupWaitlist } from "api/coursegroups";
import {
  useRecipientSelection,
  RecipientSelectionControls,
  RecipientList,
  RecipientListItem,
  RecipientSelectionMode,
} from "components/RecipientSelector";
import { useNewsletterSubscriptions } from "api/shop";

interface RecipientSelectorProps {
  name?: string;
  error?: string;
  /**
   * Optional array of user IDs to prepopulate the recipient list.
   * If not provided, will check for 'recipients' query parameter (comma-separated IDs).
   * Example URL: /scheduled-emails/new?recipients=123,456,789
   */
  initialRecipientIds?: number[];
}

type SpecialMode = "none" | "newsletter" | "waitlist";

export const RecipientSelector = ({ name = "recipients", error, initialRecipientIds }: RecipientSelectorProps) => {
  const { options: userOptions } = useUsersOptions();
  const [searchParams] = useSearchParams();
  const { setValue } = useFormContext();

  const waitlistCourseGroupIdParam = searchParams.get("waitlistCourseGroupId");
  const waitlistCourseGroupId = waitlistCourseGroupIdParam ? parseInt(waitlistCourseGroupIdParam) : undefined;

  const [specialMode, setSpecialMode] = useState<SpecialMode>(waitlistCourseGroupId ? "waitlist" : "none");
  const isNewsletterMode = specialMode === "newsletter";
  const isWaitlistMode = specialMode === "waitlist";

  const { total: newsletterTotal, isLoading: newsletterLoading } =
    useNewsletterSubscriptions(0, 1, isNewsletterMode);

  const { entries: waitlistEntries, isLoading: waitlistLoading } = useCourseGroupWaitlist(
    isWaitlistMode ? waitlistCourseGroupIdParam ?? undefined : undefined,
  );
  const [waitlistRecipients, setWaitlistRecipients] = useState<RecipientRequest[]>([]);
  const [waitlistInitialized, setWaitlistInitialized] = useState(false);

  useEffect(() => {
    if (isWaitlistMode && !waitlistInitialized && !waitlistLoading) {
      setWaitlistRecipients(
        waitlistEntries.map((entry) => ({ userId: null, emailAddress: entry.email, name: entry.name })),
      );
      setWaitlistInitialized(true);
    }
  }, [isWaitlistMode, waitlistInitialized, waitlistLoading, waitlistEntries]);

  const recipientIdsFromQuery = useMemo(() => {
    const recipientsParam = searchParams.get("recipients");
    if (recipientsParam) {
      return recipientsParam
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));
    }
    return undefined;
  }, [searchParams]);

  const finalInitialRecipientIds = initialRecipientIds || recipientIdsFromQuery;

  // Trick: gdy tryb specjalny (newsletter/waitlist) jest aktywny, hook zarządza polem "__special_ignored__"
  // (dummy field), żeby nie nadpisywał pola "recipients" ustawionego przez nas.
  const hookFieldName = specialMode !== "none" ? "__special_ignored__" : name;

  const {
    selectionMode: hookSelectionMode,
    pendingRecipients,
    currentRecipients,
    isLoading: hookLoading,
    handleModeChange: hookHandleModeChange,
    handleManualRecipientsChange,
    handleConfirmSelection,
    handleRemoveRecipient,
    handleAddToManual,
    courseOptions,
    courseGroupOptions,
    availableUserOptions,
    coursesLoading,
    courseGroupsLoading,
  } = useRecipientSelection<number>({
    name: hookFieldName,
    convertFromEmailRecipient: (recipient: EmailRecipientApi) => recipient.id,
    getUserId: (recipientId: number) => recipientId,
    initialRecipientIds: finalInitialRecipientIds,
  });

  const selectionMode: RecipientSelectionMode = isNewsletterMode
    ? "newsletter"
    : isWaitlistMode
      ? "waitlist"
      : hookSelectionMode;

  const pendingModeRef = useRef<RecipientSelectionMode | null>(null);

  useEffect(() => {
    if (isNewsletterMode) {
      setValue("isNewsletterSend", true, { shouldValidate: false });
      setValue(name, [], { shouldValidate: false });
    } else {
      setValue("isNewsletterSend", false, { shouldValidate: false });
    }
  }, [isNewsletterMode, name, setValue]);

  useEffect(() => {
    if (isWaitlistMode) {
      setValue(name, waitlistRecipients, { shouldValidate: true });
    }
  }, [isWaitlistMode, waitlistRecipients, name, setValue]);

  // Po wyjściu z trybu specjalnego: przekaż sterowanie hookowi gdy hookFieldName jest już zaktualizowany
  useEffect(() => {
    if (specialMode === "none" && pendingModeRef.current !== null) {
      hookHandleModeChange(pendingModeRef.current);
      pendingModeRef.current = null;
    }
  }, [specialMode, hookHandleModeChange]);

  const handleModeChange = (mode: RecipientSelectionMode) => {
    if (mode === "newsletter") {
      setSpecialMode("newsletter");
      return;
    }
    if (specialMode !== "none") {
      // Wychodzenie z trybu specjalnego: wyczyść pole i przekaż sterowanie hookowi
      pendingModeRef.current = mode;
      setSpecialMode("none");
      setValue(name, [], { shouldValidate: false });
    } else {
      hookHandleModeChange(mode);
    }
  };

  const handleRemoveWaitlistRecipient = (index: number) => {
    setWaitlistRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  // Konwertuj odbiorców do formatu wyświetlania
  const displayRecipients: RecipientListItem[] = useMemo(() => {
    if (isNewsletterMode) return []; // newsletter ma własny render
    if (isWaitlistMode) {
      return waitlistRecipients.map((recipient, index) => ({
        id: index,
        fullName: recipient.name || recipient.emailAddress,
        email: recipient.emailAddress,
      }));
    }
    return currentRecipients
      .map((recipientId) => {
        const option = userOptions.find((opt) => parseInt(String(opt.value)) === recipientId);
        if (!option) return null;
        return {
          id: recipientId,
          fullName: option.label,
          email: option.user?.email || "",
        };
      })
      .filter(Boolean) as RecipientListItem[];
  }, [currentRecipients, userOptions, isNewsletterMode, isWaitlistMode, waitlistRecipients]);

  const isLoading = hookLoading || (isWaitlistMode && waitlistLoading);

  return (
    <div className="space-y-4">
      <div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Lewa strona - kontrolki wyboru */}
          <div className="space-y-4">
            <RecipientSelectionControls
              selectionMode={selectionMode}
              onModeChange={handleModeChange}
              courseOptions={courseOptions}
              coursesLoading={coursesLoading}
              courseGroupOptions={courseGroupOptions}
              courseGroupsLoading={courseGroupsLoading}
              availableUserOptions={availableUserOptions}
              pendingRecipients={pendingRecipients}
              onManualRecipientsChange={handleManualRecipientsChange}
              onConfirmSelection={handleConfirmSelection}
              showModeDescriptions={true}
            />

            {isWaitlistMode && (
              <div className="rounded-md bg-blue-50 p-3 dark:bg-blue-900/20">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>Lista oczekujących:</strong> Odbiorcy wczytani z listy oczekujących kursu. Możesz usunąć
                  poszczególne osoby przed wysyłką.
                </p>
              </div>
            )}

            {/* Limit info */}
            {!isNewsletterMode && (
              <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-800">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong>Limit odbiorców:</strong> Możesz wybrać maksymalnie 1000 odbiorców na jeden email.
                </p>
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200">
                  Aktualnie wybrano: {displayRecipients.length} / 1000 odbiorców
                </p>
              </div>
            )}

            {error && (
              <div className="rounded-md bg-red-50 p-3 dark:bg-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
          </div>

          {/* Prawa strona */}
          {isNewsletterMode ? (
            <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-900/20">
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 dark:bg-indigo-500">
                  <span className="text-xs font-bold text-white">✉</span>
                </div>
                <h4 className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">
                  Lista newsletterowa
                  {newsletterLoading ? " — ładowanie..." : ` (${newsletterTotal} subskrybentów)`}
                </h4>
              </div>
              {newsletterTotal === 0 && !newsletterLoading ? (
                <p className="text-sm text-indigo-700 dark:text-indigo-300">
                  Brak subskrybentów na liście newsletterowej. Nikt nie otrzyma tego emaila.
                </p>
              ) : (
                <p className="text-xs text-indigo-700 dark:text-indigo-300">
                  <strong>Wszyscy subskrybenci newslettera otrzymają ten email.</strong> Lista jest tylko do odczytu. Serwer pobierze odbiorców w momencie wysyłki.
                </p>
              )}
            </div>
          ) : isWaitlistMode && waitlistLoading ? (
            <PaziaLoader position="center" />
          ) : (
            displayRecipients.length > 0 && (
              <div className="rounded-lg border-2 border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600 dark:bg-green-500">
                      <span className="text-xs font-bold text-white">✓</span>
                    </div>
                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">
                      Finalna lista odbiorców ({displayRecipients.length}){isLoading && " - ładowanie..."}
                    </h4>
                  </div>
                  {(selectionMode === "course" || selectionMode === "coursegroup") && (
                    <PaziaButton type="tertiary" onClick={handleAddToManual} disabled={isLoading}>
                      Dodaj do ręcznej selekcji
                    </PaziaButton>
                  )}
                </div>

                <p className="mb-3 text-xs text-green-700 dark:text-green-300">
                  <strong>Te osoby otrzymają email:</strong> Lista poniżej zawiera wszystkich odbiorców, którzy dostaną
                  zaplanowany email.
                </p>

                <RecipientList
                  recipients={displayRecipients}
                  selectionMode={selectionMode}
                  onRemoveRecipient={isWaitlistMode ? handleRemoveWaitlistRecipient : handleRemoveRecipient}
                  renderRecipientInfo={(recipient) => (
                    <>
                      <span>{recipient.fullName}</span>
                      <span className="text-gray-500 dark:text-gray-400">({recipient.email})</span>
                    </>
                  )}
                />
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
